/* ==========================================================================
   VIVANDI Hair Solution Finder — Quiz Engine
   Namespaced in an IIFE so it never leaks into Beautify's global scope.

   HOW THIS WORKS
   1. QUESTIONS defines the 12 questions (Q6 branches by gender).
   2. computeProfile() turns the collected answers into a small set of
      "role/tier" keys, e.g. foundation_standard, topical_intensive.
   3. Those keys are looked up in window.__vqProductMap, which is JSON
      you render from a Shopify Metaobject in the Custom Liquid block
      (see custom-liquid-block.liquid). That's the only place real
      product data lives, so you can swap products in Shopify admin
      without touching this file.
   ========================================================================== */

(function () {
  "use strict";

  // ------------------------------------------------------------------
  // 1. QUESTION DATA
  // ------------------------------------------------------------------
  var QUESTIONS = [
    {
      id: "q1",
      type: "single",
      text: "Who are you shopping for?",
      options: [
        { value: "myself", label: "Myself" },
        { value: "husband", label: "My Husband" },
        { value: "wife", label: "My Wife" },
        { value: "child", label: "My Child" },
        { value: "someone_else", label: "Someone Else" }
      ]
    },
    {
      id: "q2",
      type: "single",
      text: "What is your gender?",
      options: [
        { value: "female", label: "Female" },
        { value: "male", label: "Male" }
      ]
    },
    {
      id: "q3",
      type: "single",
      text: "What is your age?",
      options: [
        { value: "under18", label: "Under 18" },
        { value: "18-29", label: "18–29" },
        { value: "30-39", label: "30–39" },
        { value: "40-49", label: "40–49" },
        { value: "50-59", label: "50–59" },
        { value: "60plus", label: "60+" }
      ]
    },
    {
      id: "q4",
      type: "single",
      text: "What best describes your biggest concern?",
      options: [
        { value: "shedding", label: "Increased hair shedding" },
        { value: "thinning", label: "Hair is becoming thinner" },
        { value: "receding", label: "Receding hairline" },
        { value: "bald_spots", label: "Bald spots" },
        { value: "weak_brittle", label: "Weak or brittle hair" },
        { value: "dry_scalp", label: "Dry scalp" },
        { value: "oily_scalp", label: "Oily scalp" },
        { value: "volume", label: "Loss of hair volume" }
      ]
    },
    {
      id: "q5",
      type: "single",
      text: "How long have you noticed this problem?",
      options: [
        { value: "lt3m", label: "Less than 3 months" },
        { value: "3-6m", label: "3–6 months" },
        { value: "6-12m", label: "6–12 months" },
        { value: "1-2y", label: "1–2 years" },
        { value: "gt2y", label: "More than 2 years" }
      ]
    },
    {
      id: "q6",
      type: "single",
      text: "Where is your hair thinning the most?",
      optionsByGender: {
        female: [
          { value: "overall", label: "Overall thinning" },
          { value: "part_line", label: "Part line widening" },
          { value: "crown", label: "Crown" },
          { value: "temples", label: "Temples" },
          { value: "entire_scalp", label: "Entire scalp" }
        ],
        male: [
          { value: "hairline", label: "Hairline" },
          { value: "crown", label: "Crown" },
          { value: "top_scalp", label: "Top of scalp" },
          { value: "diffuse", label: "Diffuse thinning" }
        ]
      }
    },
    {
      id: "q7",
      type: "multi",
      text: "Which of these may apply to you?",
      note: "Select all that apply. This quiz is not intended to diagnose medical conditions.",
      options: [
        { value: "stress", label: "Stress" },
        { value: "menopause", label: "Menopause" },
        { value: "postpartum", label: "Postpartum" },
        { value: "weight_loss", label: "Weight loss" },
        { value: "diet", label: "Diet changes" },
        { value: "family_history", label: "Family history" },
        { value: "thyroid", label: "Thyroid issues" },
        { value: "none", label: "None of these" }
      ]
    },
    {
      id: "q8",
      type: "single",
      text: "Have you used hair loss products before?",
      options: [
        { value: "never", label: "Never" },
        { value: "minoxidil", label: "Minoxidil" },
        { value: "supplements", label: "Supplements" },
        { value: "laser", label: "Laser therapy" },
        { value: "prescription", label: "Prescription treatment" },
        { value: "several", label: "Several products" }
      ]
    },
    {
      id: "q9",
      type: "single",
      text: "What type of routine do you prefer?",
      options: [
        { value: "simple", label: "Very simple (1 minute)" },
        { value: "moderate", label: "Moderate (5 minutes)" },
        { value: "complete", label: "Complete hair care routine" }
      ]
    },
    {
      id: "q10",
      type: "single",
      text: "How committed are you?",
      options: [
        { value: "fastest", label: "I want the fastest comprehensive approach" },
        { value: "consistent", label: "I'm willing to be consistent for several months" },
        { value: "small", label: "I want to start small" }
      ]
    },
    {
      id: "q11",
      type: "single",
      text: "What is your budget?",
      options: [
        { value: "under100", label: "Under $100" },
        { value: "100-200", label: "$100–200" },
        { value: "200-350", label: "$200–350" },
        { value: "over350", label: "Over $350" }
      ]
    },
    {
      id: "q12",
      type: "single",
      text: "Are you interested in professional-strength products recommended by hair specialists?",
      options: [
        { value: "yes", label: "Yes" },
        { value: "maybe", label: "Maybe" },
        { value: "no_pref", label: "No Preference" }
      ]
    }
  ];

  // ------------------------------------------------------------------
  // 2. SCORING — turns answers into role/tier keys
  //    Edit the thresholds below to tune recommendations. This is the
  //    ONLY place business logic lives; product data lives in Shopify.
  // ------------------------------------------------------------------
  var DURATION_SCORE = { lt3m: 1, "3-6m": 2, "6-12m": 3, "1-2y": 4, gt2y: 5 };
  var BUDGET_SCORE = { under100: 1, "100-200": 2, "200-350": 3, over350: 4 };

  function computeProfile(answers) {
    var concern = answers.q4;
    var durationScore = DURATION_SCORE[answers.q5] || 2;
    var budgetScore = BUDGET_SCORE[answers.q11] || 1;
    var routinePref = answers.q9;
    var commitment = answers.q10;
    var wantsPro = answers.q12;

    var severity = durationScore + (commitment === "fastest" ? 2 : commitment === "consistent" ? 1 : 0);

    // Foundation (daily supplement) — always included, tier depends on budget/routine
    var foundationTier = budgetScore === 1 && routinePref === "simple" ? "budget" : "standard";

    // Intensive topical program — only for moderate+ severity, budget allows, not the "simple" routine
    var includeTopical = severity >= 3 && budgetScore >= 2 && routinePref !== "simple";
    var topicalTier = includeTopical ? (severity >= 5 && budgetScore >= 3 ? "intensive" : "standard") : null;

    // Daily care (shampoo) — scalp-related concerns, or whenever routine isn't the bare-minimum tier
    var includeDaily =
      ["dry_scalp", "oily_scalp", "volume", "thinning"].indexOf(concern) !== -1 || routinePref !== "simple";

    // Optional advanced tech (device) — explicit interest + budget headroom
    var includeDevice = wantsPro === "yes" && budgetScore >= 3;

    var roles = [];
    roles.push({ role: "foundation", the_key: "foundation_" + foundationTier });
    if (includeTopical) roles.push({ role: "topical", the_key: "topical_" + topicalTier });
    if (includeDaily) roles.push({ role: "daily", the_key: "daily_default" });
    if (includeDevice) roles.push({ role: "device", the_key: "device_default" });

    return {
      concern: concern,
      severity: severity,
      includeTopical: includeTopical,
      includeDaily: includeDaily,
      includeDevice: includeDevice,
      roles: roles,
      answers: answers
    };
  }

  var CONCERN_LABELS = {
    shedding: "Increased hair shedding",
    thinning: "Hair thinning",
    receding: "Receding hairline",
    bald_spots: "Bald spots",
    weak_brittle: "Weak, brittle hair",
    dry_scalp: "Dry scalp",
    oily_scalp: "Oily scalp",
    volume: "Loss of hair volume"
  };

  function buildProfileSummary(profile) {
    var items = [];
    items.push(CONCERN_LABELS[profile.concern] || "Hair concern noted");
    items.push(profile.severity >= 4 ? "Longer-term, moderate to significant concern" : "Early-stage concern");
    items.push(
      profile.includeTopical ? "Looking for a comprehensive routine" : "Prefers a lighter, simpler routine"
    );
    items.push(
      profile.answers.q10 === "fastest"
        ? "Wants the fastest possible results"
        : "Ready for long-term consistency"
    );
    return items;
  }

  // Static fallback copy per concern, used until/unless you wire up AI personalization (phase 2)
  var SUMMARY_COPY = {
    shedding: "you're dealing with increased shedding and want a routine that supports healthier hair growth over time.",
    thinning: "you've noticed your hair thinning and you're looking for a plan that addresses it comprehensively.",
    receding: "you're seeing your hairline recede and want a structured program to help slow further changes.",
    bald_spots: "you've noticed bald spots and want a targeted, consistent approach.",
    weak_brittle: "your hair feels weak or brittle and you're looking to strengthen it from the inside out.",
    dry_scalp: "a dry scalp is part of the picture, so scalp care is built into your daily routine.",
    oily_scalp: "an oily scalp is part of the picture, so scalp care is built into your daily routine.",
    volume: "you're looking to restore volume and want a plan that supports fuller-looking hair."
  };

  function buildAiNote(profile) {
    var base = SUMMARY_COPY[profile.concern] || "you're looking for a plan tailored to your hair goals.";
    var pace =
      profile.answers.q10 === "fastest"
        ? "Your program leans toward faster, more intensive support."
        : profile.answers.q10 === "small"
        ? "Your program starts light, with room to build up over time."
        : "Your program is designed for steady, consistent progress.";
    return "Based on your answers, " + base + " " + pace;
  }

  // ------------------------------------------------------------------
  // 3. STATE + DOM RENDERING
  // ------------------------------------------------------------------
  var root = document.getElementById("vivandi-quiz-root");
  if (!root) return;

  var state = {
    screen: "intro", // intro | question | lead | results
    stepIndex: 0,
    answers: {}
  };

  function getActiveQuestions() {
    // Materialize Q6's gender-specific options once we know the gender
    return QUESTIONS.map(function (q) {
      if (q.id === "q6" && q.optionsByGender) {
        var gender = state.answers.q2 === "male" ? "male" : "female";
        return Object.assign({}, q, { options: q.optionsByGender[gender] });
      }
      return q;
    });
  }

  function render() {
    if (state.screen === "intro") return renderIntro();
    if (state.screen === "question") return renderQuestion();
    if (state.screen === "lead") return renderLead();
    if (state.screen === "results") return renderResults();
  }

  function renderIntro() {
    root.innerHTML =
      '<div class="vq-intro">' +
      "<h1>Find Your Personalized Hair Restoration Plan</h1>" +
      "<p>Answer 12 quick questions and receive a customized recommendation based on your hair concerns, lifestyle, and goals.</p>" +
      '<span class="vq-eta">⏱ Takes less than 2 minutes</span><br/>' +
      '<button class="vq-start-btn" id="vq-start-btn">Start My Assessment</button>' +
      "</div>";
    document.getElementById("vq-start-btn").addEventListener("click", function () {
      state.screen = "question";
      state.stepIndex = 0;
      render();
    });
  }

  function renderQuestion() {
    var questions = getActiveQuestions();
    var q = questions[state.stepIndex];
    var total = questions.length;
    var pct = Math.round(((state.stepIndex) / total) * 100);

    var optionsHtml = q.options
      .map(function (opt) {
        var inputType = q.type === "multi" ? "checkbox" : "radio";
        var currentVal = state.answers[q.id];
        var selected =
          q.type === "multi"
            ? Array.isArray(currentVal) && currentVal.indexOf(opt.value) !== -1
            : currentVal === opt.value;
        return (
          '<div class="vq-option' +
          (selected ? " vq-selected" : "") +
          '" data-value="' +
          opt.value +
          '">' +
          '<input type="' +
          inputType +
          '" name="' +
          q.id +
          '" id="' +
          q.id +
          "_" +
          opt.value +
          '" ' +
          (selected ? "checked" : "") +
          "/>" +
          '<label for="' +
          q.id +
          "_" +
          opt.value +
          '">' +
          opt.label +
          "</label>" +
          "</div>"
        );
      })
      .join("");

    root.innerHTML =
      '<div class="vq-progress-wrap">' +
      '<div class="vq-progress-track"><div class="vq-progress-fill" style="width:' +
      pct +
      '%"></div></div>' +
      '<div class="vq-progress-label">Question ' +
      (state.stepIndex + 1) +
      " of " +
      total +
      "</div>" +
      "</div>" +
      '<div class="vq-question">' +
      "<h2>" +
      q.text +
      "</h2>" +
      (q.note ? '<div class="vq-note">' + q.note + "</div>" : "") +
      '<div class="vq-options">' +
      optionsHtml +
      "</div>" +
      '<div class="vq-nav">' +
      (state.stepIndex > 0 ? '<button class="vq-btn vq-btn-secondary" id="vq-back-btn">← Back</button>' : "<span></span>") +
      '<button class="vq-btn vq-btn-primary" id="vq-next-btn"' +
      (isAnswered(q) ? "" : " disabled") +
      ">" +
      (state.stepIndex === total - 1 ? "See My Results" : "Next") +
      "</button>" +
      "</div>" +
      "</div>";

    root.querySelectorAll(".vq-option").forEach(function (el) {
      el.addEventListener("click", function () {
        handleSelect(q, el.getAttribute("data-value"));
      });
    });

    var backBtn = document.getElementById("vq-back-btn");
    if (backBtn) backBtn.addEventListener("click", goBack);

    document.getElementById("vq-next-btn").addEventListener("click", goNext);
  }

  function isAnswered(q) {
    var val = state.answers[q.id];
    if (q.type === "multi") return Array.isArray(val) && val.length > 0;
    return !!val;
  }

  function handleSelect(q, value) {
    if (q.type === "multi") {
      var current = state.answers[q.id] || [];
      if (value === "none") {
        current = current.indexOf("none") !== -1 ? [] : ["none"];
      } else {
        current = current.filter(function (v) {
          return v !== "none";
        });
        var idx = current.indexOf(value);
        if (idx === -1) current.push(value);
        else current.splice(idx, 1);
      }
      state.answers[q.id] = current;
    } else {
      state.answers[q.id] = value;
    }
    renderQuestion();
  }

  function goBack() {
    state.stepIndex -= 1;
    render();
  }

  function goNext() {
    var questions = getActiveQuestions();
    if (state.stepIndex < questions.length - 1) {
      state.stepIndex += 1;
      render();
    } else {
      state.screen = "lead";
      render();
    }
  }

  function renderLead() {
    root.innerHTML =
      '<div class="vq-lead">' +
      "<h2>Where should we send your personalized hair plan?</h2>" +
      "<p>We'll email your full recommendation so you can save and revisit it.</p>" +
      '<div class="vq-field"><label for="vq-name">Name</label><input type="text" id="vq-name" /></div>' +
      '<div class="vq-field"><label for="vq-email">Email</label><input type="email" id="vq-email" required /></div>' +
      '<div class="vq-field"><label for="vq-phone">Phone (optional)</label><input type="tel" id="vq-phone" /></div>' +
      '<div class="vq-checkbox-row"><input type="checkbox" id="vq-optin" checked/><label for="vq-optin">Email me educational tips and exclusive offers</label></div>' +
      '<div class="vq-nav">' +
      '<button class="vq-btn vq-btn-secondary" id="vq-lead-back">← Back</button>' +
      '<button class="vq-btn vq-btn-primary" id="vq-lead-submit">Get My Results</button>' +
      "</div>" +
      "</div>";

    document.getElementById("vq-lead-back").addEventListener("click", function () {
      state.screen = "question";
      render();
    });

    document.getElementById("vq-lead-submit").addEventListener("click", function () {
      var email = document.getElementById("vq-email").value.trim();
      if (!email || email.indexOf("@") === -1) {
        document.getElementById("vq-email").style.borderColor = "#c0392b";
        return;
      }
      var lead = {
        name: document.getElementById("vq-name").value.trim(),
        email: email,
        phone: document.getElementById("vq-phone").value.trim(),
        optin: document.getElementById("vq-optin").checked
      };
      submitLead(lead, state.answers);
      state.screen = "results";
      render();
    });
  }

  // TODO: wire this up to whatever email/CRM tool you're using
  // (Klaviyo list API, Shopify customer creation, etc). Left as a
  // stub so the quiz works end-to-end before that's connected.
  function submitLead(lead, answers) {
    console.log("Lead captured:", lead, answers);
    // Example (Klaviyo, adjust to your actual list + public key):
    // fetch('https://a.klaviyo.com/api/subscriptions', { method: 'POST', ... })
  }

  function getProductMap() {
    var el = document.getElementById("quiz-product-map");
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error("Quiz product map JSON is invalid:", e);
      return {};
    }
  }

  var ROLE_LABELS = {
    foundation: "Foundation",
    topical: "Intensive Topical Care",
    daily: "Daily Hair Care",
    device: "Optional Advanced Technology"
  };

  function renderResults() {
    var profile = computeProfile(state.answers);
    var productMap = getProductMap();
    var summary = buildProfileSummary(profile);
    var aiNote = buildAiNote(profile);

    var cardsHtml = profile.roles
      .map(function (r) {
        var product = productMap[r.the_key];
        if (!product) return ""; // no product mapped yet for this key — skip gracefully
        return (
          '<div class="vq-product-card">' +
          (product.image ? '<img src="' + product.image + '" alt="' + product.title + '"/>' : "") +
          "<div>" +
          '<div class="vq-product-role">' +
          ROLE_LABELS[r.role] +
          "</div>" +
          '<div class="vq-product-title">' +
          product.title +
          "</div>" +
          (product.price ? '<div class="vq-product-price">' + product.price + "</div>" : "") +
          (product.url
            ? '<a class="vq-view-btn" href="' + product.url + '">View product →</a>'
            : "") +
          "</div>" +
          "</div>"
        );
      })
      .join("");

    root.innerHTML =
      '<div class="vq-results">' +
      "<h2>Your Personalized Hair Restoration Program</h2>" +
      '<div class="vq-results-sub">Your Hair Profile</div>' +
      '<ul class="vq-profile-list">' +
      summary.map(function (s) { return "<li>" + s + "</li>"; }).join("") +
      "</ul>" +
      '<div class="vq-ai-note">' + aiNote + "</div>" +
      "<h3>Recommended Program</h3>" +
      (cardsHtml || "<p>We're finalizing your product matches.</p>") +
      "</div>";
  }

  // ------------------------------------------------------------------
  // Kick off
  // ------------------------------------------------------------------
  render();
})();